import type { ArrowShape } from './arrow';
import { assertValidBoard, normalizeBoard, type BoardData } from './board';
import { base64Decode, base64Encode, packBits, unpackBits } from './codec';
import { DIRECTION_INDEX, directionFromIndex } from './direction';
import { hashBytes64, toHex64 } from './hash';
import { isValidIdentity } from './identity';
import type { GeneratedLevel, SerializedBoard, SerializedGeneratedLevel } from './level';

/**
 * Canonical serialization.
 *
 * Fixed field order, explicit big-endian integers, row-major mask ordering and arrows in
 * arrow-id order. Nothing here depends on object property enumeration, locale or float
 * formatting, so the same board produces the same bytes — and therefore the same hash — in
 * every runtime.
 */

/** Bumped to 2 when arrows became multi-cell paths; v1 boards cannot be read. */
export const BOARD_SCHEMA_VERSION = 2;
export const LEVEL_SCHEMA_VERSION = 2;

const MAGIC = [0x4f, 0x42, 0x42, 0x32]; // "OBB2"

export function canonicalBoardBytes(board: BoardData): Uint8Array {
  const packedMask = packBits(board.activeMask);
  let arrowBytes = 0;
  for (const arrow of board.arrows) {
    // direction, cell count, then two bytes per cell.
    arrowBytes += 2 + arrow.cells.length * 2;
  }

  const size = MAGIC.length + 1 + 2 + 4 + packedMask.length + 4 + arrowBytes;
  const bytes = new Uint8Array(size);
  let offset = 0;

  for (const byte of MAGIC) bytes[offset++] = byte;
  bytes[offset++] = BOARD_SCHEMA_VERSION;
  bytes[offset++] = board.width;
  bytes[offset++] = board.height;

  offset = writeUint32(bytes, offset, packedMask.length);
  bytes.set(packedMask, offset);
  offset += packedMask.length;

  offset = writeUint32(bytes, offset, board.arrows.length);
  for (const arrow of board.arrows) {
    bytes[offset++] = DIRECTION_INDEX[arrow.direction];
    bytes[offset++] = arrow.cells.length;
    for (const cell of arrow.cells) {
      bytes[offset++] = cell.x;
      bytes[offset++] = cell.y;
    }
  }
  return bytes;
}

function writeUint32(bytes: Uint8Array, offset: number, value: number): number {
  bytes[offset] = (value >>> 24) & 0xff;
  bytes[offset + 1] = (value >>> 16) & 0xff;
  bytes[offset + 2] = (value >>> 8) & 0xff;
  bytes[offset + 3] = value & 0xff;
  return offset + 4;
}

/** Stable 16-character content hash of the logical board. */
export function computeBoardHash(board: BoardData): string {
  return toHex64(hashBytes64(canonicalBoardBytes(board)));
}

export function serializeBoard(board: BoardData): SerializedBoard {
  // Flat encoding per arrow: directionIndex, cellCount, then x/y pairs from tail to head.
  const arrows: number[] = [];
  for (const arrow of board.arrows) {
    arrows.push(DIRECTION_INDEX[arrow.direction], arrow.cells.length);
    for (const cell of arrow.cells) arrows.push(cell.x, cell.y);
  }
  return {
    schemaVersion: BOARD_SCHEMA_VERSION,
    width: board.width,
    height: board.height,
    mask: base64Encode(packBits(board.activeMask)),
    arrows,
  };
}

export function deserializeBoard(serialized: SerializedBoard): BoardData {
  if (serialized.schemaVersion !== BOARD_SCHEMA_VERSION) {
    throw new Error(`Unsupported board schema version ${serialized.schemaVersion}`);
  }
  const { width, height } = serialized;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new Error('Invalid board dimensions');
  }
  const activeMask = unpackBits(base64Decode(serialized.mask), width * height);
  const arrows: ArrowShape[] = [];
  let cursor = 0;
  while (cursor < serialized.arrows.length) {
    const direction = directionFromIndex(serialized.arrows[cursor]);
    const cellCount = serialized.arrows[cursor + 1];
    cursor += 2;
    if (!Number.isInteger(cellCount) || cellCount <= 0) {
      throw new Error('Invalid arrow cell count');
    }
    if (cursor + cellCount * 2 > serialized.arrows.length) {
      throw new Error('Truncated arrow payload');
    }
    const cells = [];
    for (let i = 0; i < cellCount; i += 1) {
      cells.push({ x: serialized.arrows[cursor], y: serialized.arrows[cursor + 1] });
      cursor += 2;
    }
    arrows.push({ cells, direction });
  }
  const board = normalizeBoard(width, height, activeMask, arrows);
  assertValidBoard(board);
  return board;
}

export function serializeLevel(level: GeneratedLevel): SerializedGeneratedLevel {
  return {
    schemaVersion: LEVEL_SCHEMA_VERSION,
    identity: level.identity,
    rootSeed: level.rootSeed,
    stencilId: level.stencilId,
    recipe: level.recipe,
    board: serializeBoard(level.board),
    canonicalSolution: [...level.canonicalSolution],
    analysis: level.analysis,
    boardHash: level.boardHash,
    candidateIndex: level.candidateIndex,
    usedFallback: level.usedFallback,
  };
}

/**
 * Rebuilds a level from untrusted input (worker message, IndexedDB record, debug import).
 * The board hash is always recomputed and compared: a mismatch means the record was
 * produced by different code and must not be used.
 */
export function deserializeLevel(serialized: SerializedGeneratedLevel): GeneratedLevel {
  if (serialized.schemaVersion !== LEVEL_SCHEMA_VERSION) {
    throw new Error(`Unsupported level schema version ${serialized.schemaVersion}`);
  }
  if (!isValidIdentity(serialized.identity)) {
    throw new Error('Invalid level identity');
  }
  const board = deserializeBoard(serialized.board);
  const boardHash = computeBoardHash(board);
  if (boardHash !== serialized.boardHash) {
    throw new Error(`Board hash mismatch: expected ${serialized.boardHash}, computed ${boardHash}`);
  }
  if (!Array.isArray(serialized.canonicalSolution)) {
    throw new Error('Invalid canonical solution');
  }
  return {
    identity: serialized.identity,
    rootSeed: serialized.rootSeed,
    stencilId: serialized.stencilId,
    recipe: serialized.recipe,
    board,
    canonicalSolution: [...serialized.canonicalSolution],
    analysis: serialized.analysis,
    boardHash,
    candidateIndex: serialized.candidateIndex,
    usedFallback: serialized.usedFallback,
  };
}
